import { useCallback, useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Tracks a set of selected row ids. Deliberately dumb — it doesn't know what
 * rows exist, so a filter/tab change or a row disappearing (deleted, paged
 * out) can leave a stale id selected. Callers that care (the bar's count,
 * "select all") should derive against their own currently-visible id list,
 * and call clear() after anything that changes what's on screen (switching
 * tabs/filters, a bulk action finishing).
 */
export function useSelection() {
  const [selected, setSelected] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const setMany = useCallback((ids, on) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);
  const isSelected = useCallback((id) => selected.has(id), [selected]);

  return { selected, count: selected.size, toggle, setMany, clear, isSelected };
}

/** Small checkbox box used for both the header "select all" and each row —
 *  a plain div rather than the Radix Checkbox so it can sit inline in a
 *  table header/row without extra wiring. */
export function SelectBox({ checked, indeterminate, onClick, className }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      aria-checked={indeterminate ? "mixed" : checked}
      role="checkbox"
      className={cn(
        "w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors",
        checked || indeterminate ? "bg-brand border-brand" : "bg-card border-[#b9c4bd] hover:border-brand",
        className
      )}
    >
      {checked && !indeterminate && (
        <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-2.5 h-2.5">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {indeterminate && <div className="w-2 h-[2px] bg-white rounded-full" />}
    </button>
  );
}

/**
 * The toolbar that replaces a page's filter/view-toggle row once at least
 * one row is selected. `actions` is [{ key, label, icon, onClick, tone,
 * disabled, title }] — tone "danger" tints the button rose.
 */
export function BulkActionBar({ count, onClear, actions, noun = "selected" }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-brand-ring bg-brand-light px-3.5 h-11 mb-4 flex-wrap">
      <span className="text-[12.5px] font-semibold text-brand-hover whitespace-nowrap">
        {count} {noun}
      </span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {actions.map(({ key, label, icon: Icon, onClick, tone, disabled, title }) => (
          <button
            key={key}
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            className={cn(
              "inline-flex items-center gap-1.5 h-[30px] px-3 rounded-md text-[12px] font-semibold border bg-card whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
              tone === "danger"
                ? "text-stock-out border-rose-200 hover:bg-rose-50"
                : "text-ink border-line hover:bg-surface"
            )}
          >
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 h-[30px] px-2.5 rounded-md text-[11.5px] font-semibold text-muted2 hover:text-ink"
        >
          <X className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
}

/**
 * Runs one async op per item and never throws — a handful of failures (a
 * dependent record, a stale row) shouldn't stop the rest from going
 * through. Callers report ok/failed counts with their own toast wording.
 */
export async function runBulk(items, fn) {
  const results = await Promise.allSettled(items.map((item) => fn(item)));
  const ok = results.filter((r) => r.status === "fulfilled").length;
  const failed = results.length - ok;
  return { ok, failed, results };
}
